import { Injectable, inject } from "@angular/core";
import { LgTranslateService } from "@logex/framework/lg-localization";
import * as _ from "lodash";

import {
    FilterFactoryCreatorBase,
    IFilterFactoryEntry,
    MapFilterDefinition,
    MapFilterStore,
    SupportedFilters
} from "./bases/filter-factory-base";
import { Filters as FilterId } from "./types/filters";

import {
    ICheckboxFilterDefinition,
    IComboFilter2Definition,
    IFilterList,
    IInputRangeFilterDefinition,
    INormalizedFilterDefinition,
    LgFilterSetService
} from "@logex/framework/lg-filterset";
import { AppDefinitions, DefinitionKey } from "./app-definitions.service";

// ---------------------------------------------------------------------------------------------
//  Implementation of the FilterFactory service
// ---------------------------------------------------------------------------------------------

@Injectable({ providedIn: "root" })
export class FilterFactory {
    private _appDefinitions = inject(AppDefinitions);
    private _filterSetService = inject(LgFilterSetService);
    private _lgTranslate = inject(LgTranslateService);

    define(): FilterFactoryCreator<any, any> {
        return new FilterFactoryCreator(
            this._filterSetService,
            this._lgTranslate,
            this._appDefinitions
        );
    }
}

// ---------------------------------------------------------------------------------------------
type Combo2Parameters<T> = Pick<
    IComboFilter2Definition<T>,
    | "source"
    | "onBecameActive"
    | "onBecameActiveLimit"
    | "onChanged"
    | "visible"
    | "nameLC"
    | "showSelected"
    | "maxRows"
    | "showDataCounts"
    | "popupItemCustomization"
>;

type CheckboxParameters = Pick<
    ICheckboxFilterDefinition,
    | "uncheckedFilters"
    | "default"
    | "previewTextLC"
    | "previewPopup"
    | "nameLC"
    | "onChanged"
    | "visible"
>;

// type SelectableCombo2Parameters<T> = Pick<Filters.ISelectableComboFilter2Definition<T>, "source" | "onBecameActive" | "onBecameActiveLimit" | "onChanged" | "visible" | "nameLC" | "onSelectionChanged" | "onSelectionRemoved">;

type InputRangeParameters = Pick<
    IInputRangeFilterDefinition,
    "min" | "max" | "format" | "decimals" | "default" | "onChanged" | "visible" | "nameLC"
>;

// ---------------------------------------------------------------------------------------------
//  Implementation of the creator.
// ---------------------------------------------------------------------------------------------
export class FilterFactoryCreator<
    Definitions extends Record<string, INormalizedFilterDefinition>,
    Filters extends IFilterList = {}
> extends FilterFactoryCreatorBase {
    public static readonly MAX_TAGS_SHOWN_DEFAULT = 10;
    public static readonly TAGS_LINES_DEFAULT = 2;
    constructor(
        _filterSetService: LgFilterSetService,
        _lgTranslate: LgTranslateService,
        private _appDefinitions: AppDefinitions
    ) {
        super(_filterSetService, _lgTranslate);
    }

    // ---------------------------------------------------------------------------------------------
    create(context: any) {
        return this._create<Definitions, Filters>(context);
    }

    // ----------------------------------------------------------------------------------
    //
    activityFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.ACTIVITY, params, {
            nameLC: "FW._Filters.Activity",
            mapToOptions: this.createMapper("activity"),
            maxRows: 1
        });
    }

    ageCategoryFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.AGE_CATEGORY, params, {
            nameLC: "APP._Filters.AgeCategory",
            mapToOptions: this.createMapper("ageCategory")
        });
    }

    ccs0Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.CCS0, params, {
            nameLC: "APP._Filters.Ccs0",
            idType: "string",
            mapToOptions: this.createMapper("ccs0")
        });
    }

    diagnosisGroupFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.DIAGNOSIS_GROUP, params, {
            nameLC: "FW._Filters.DiagnosisGroup",
            idType: "string",
            mapToOptions: this.createMapper("diagnosisGroup")
        });
    }

    diagnosisFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.DIAGNOSIS, params, {
            nameLC: "FW._Filters.Diagnosis",
            idType: "string",
            mapToOptions: this.createMapper("diagnosis")
        });
    }

    genderFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.GENDER, params, {
            nameLC: "APP._Filters.Gender",
            mapToOptions: this.createMapper("gender")
        });
    }

    insurerFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.INSURER, params, {
            nameLC: "APP._Filters.Insurer",
            mapToOptions: this.createMapper("insurer")
        });
    }

    locationFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.LOCATION, params, {
            nameLC: "APP._Filters.Location",
            mapToOptions: this.createMapper("location")
        });
    }

    isChronicFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.IsChronic__no"),
            1: this._lgTranslate.translate("APP._Filters.IsChronic__yes")
        };
        return this.createCombo2Number(FilterId.IS_CHRONIC, params, {
            nameLC: "APP._Filters.IsChronic",
            mapToOptions: this.createDictionaryMapper(definition)
        });
    }

    isComorbidFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.COMORBIDITY_INDEX, params, {
            nameLC: "APP._Filters.IsComorbid",
            mapToOptions: this.createMapper("comorbidityIndex")
        });
    }

    isFrailElderlyFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.IsFrailElderly__no"),
            1: this._lgTranslate.translate("APP._Filters.IsFrailElderly__yes")
        };
        return this.createCombo2Number(FilterId.IS_FRAIL_ELDERLY, params, {
            nameLC: "APP._Filters.IsFrailElderly",
            mapToOptions: this.createDictionaryMapper(definition)
        });
    }

    isNewFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.IsNew__no"),
            1: this._lgTranslate.translate("APP._Filters.IsNew__yes")
        };
        return this.createCombo2Number(FilterId.IS_NEW, params, {
            nameLC: "APP._Filters.IsNew",
            idType: "number",
            mapToOptions: this.createDictionaryMapper(definition)
        });
    }

    isComplexFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.IsComplex__no"),
            1: this._lgTranslate.translate("APP._Filters.IsComplex__yes")
        };
        return this.createCombo2Number(FilterId.IS_COMPLEX, params, {
            nameLC: "APP._Filters.IsComplex",
            mapToOptions: this.createDictionaryMapper(definition)
        });
    }

    municipalityFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.MUNICIPALITY, params, {
            nameLC: "APP._Filters.Municipality",
            idType: "string",
            mapToOptions: this.createMapper("municipality")
        });
    }

    careClusterFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.CARE_CLUSTER, params, {
            nameLC: "APP._Filters.CareCluster",
            mapToOptions: this.createMapper("careCluster")
        });
    }

    referrerTypeFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.REFERRER_TYPE, params, {
            nameLC: "APP._Filters.ReferrerType",
            idType: "string",
            mapToOptions: this.createMapper("referrerType")
        });
    }

    socialStatusFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.SOCIAL_STATUS, params, {
            nameLC: "APP._Filters.SocialStatus",
            mapToOptions: this.createMapper("socialStatus")
        });
    }

    specialismFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.SPECIALISM, params, {
            nameLC: "FW._Filters.Specialism",
            mapToOptions: this.createMapper("specialism"),
            maxRows: 1
        });
    }

    zeggFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.ZE_GG, params, {
            nameLC: "APP._Filters.Zegg",
            mapToOptions: (codes: string[]) => {
                return _.chain(codes)
                    .map(code => {
                        return { id: code, name: code, order: code };
                    })
                    .sortBy(f => f.order)
                    .value();
            },
            maxRows: 1
        });
    }

    rveFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.RVE, params, {
            nameLC: "APP._Filters.Rve",
            mapToOptions: this.createMapper("rve")
        });
    }

    hospitalAreaFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.HospitalArea_no"),
            1: this._lgTranslate.translate("APP._Filters.HospitalArea_yes")
        };
        return this.createCombo2Number(FilterId.HOSPITAL_AREA, params, {
            nameLC: "APP._Filters.HospitalArea",
            mapToOptions: this.createDictionaryMapper(definition)
        });
    }

    isUrgencyCareFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.URGENCY_CARE, params, {
            nameLC: "APP._Filters.UrgencyCare",
            mapToOptions: this.createMapper("urgencyCare"),
            tooltipLC: "APP._Filters.UrgencyCare__tooltip"
        });
    }

    isReferredFilter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.IS_REFERRED, params, {
            nameLC: "APP._Filters.IsReferred",
            mapToOptions: this.createMapper("referral")
        });
    }

    multidisciplinarySpecialismFilter(params: Combo2Parameters<string>) {
        return this.createCombo2String(FilterId.MULTIDISCIPLINARY_SPECIALISM, params, {
            nameLC: "APP._Filters.MultidisciplinarySpecialism",
            mapToOptions: this.createMapper("specialism")
        });
    }

    ac1Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.AC1, params, {
            nameLC: "APP._Filters.Ac1",
            mapToOptions: this.createMapper("ac1"),
            maxRows: 1
        });
    }

    ac2Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.AC2, params, {
            nameLC: "APP._Filters.Ac2",
            mapToOptions: this.createMapper("ac2"),
            maxRows: 1
        });
    }

    ac3Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.AC3, params, {
            nameLC: "APP._Filters.Ac3",
            mapToOptions: this.createMapper("ac3"),
            maxRows: 1
        });
    }

    trc1Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.TRC1, params, {
            nameLC: "APP._Filters.Trc1",
            mapToOptions: this.createMapper("trc1")
        });
    }

    trc2Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.TRC2, params, {
            nameLC: "APP._Filters.Trc2",
            mapToOptions: this.createMapper("trc2")
        });
    }

    trc3Filter(params: Combo2Parameters<number>) {
        return this.createCombo2Number(FilterId.TRC3, params, {
            nameLC: "APP._Filters.Trc3",
            mapToOptions: this.createMapper("trc3")
        });
    }

    pandemicCareFilter(params: Combo2Parameters<number>) {
        const definition = {
            0: this._lgTranslate.translate("APP._Filters.PandemicCare__duringCorona"),
            1: this._lgTranslate.translate("APP._Filters.PandemicCare__preCorona"),
            2: this._lgTranslate.translate("APP._Filters.PandemicCare__postCorona")
        };
        const tooltipDefinition = {
            0: "APP._Filters.PandemicCare__duringCoronaDescription",
            1: "APP._Filters.PandemicCare__preCoronaDescription",
            2: "APP._Filters.PandemicCare__postCoronaDescription"
        };
        return this.createCombo2Number(FilterId.PANDEMIC_CARE, params, {
            nameLC: "APP._Filters.PandemicCare",
            mapToOptions: (codes: number[]) => {
                return _.chain(codes)
                    .map(code => {
                        return {
                            id: code,
                            name: definition[code],
                            order: code,
                            data: { tooltipTextLC: tooltipDefinition[code] }
                        };
                    })
                    .sortBy(f => f.order)
                    .value();
            }
        });
    }

    rangeMoneyFilter(filterName: string, params: InputRangeParameters) {
        return this.addFilter(filterName, {
            ...params,
            ...{ filterType: "inputRange", min: -999999999, max: 999999999, format: "eur" }
        });
    }

    patientVolumeFilter(params: InputRangeParameters) {
        return this.addFilter(FilterId.PATIENT_VOLUME, {
            ...params,
            ...{ filterType: "inputRange", min: -9999999, max: 9999999, format: "int" }
        });
    }

    flagsFilter(params: Combo2Parameters<number>) {
        return this.flagsFilterNum(FilterId.FLAG, params, "APP._Filters.Flag", "flag");
    }

    kpiFlagsFilter(params: Combo2Parameters<string>) {
        return this.flagsFilterStr(FilterId.FLAG, params, "APP._Filters.Flag", "flag");
    }

    private _mapToFlagsOption<T extends string | number>(
        defKey: DefinitionKey & ("flag" | "bestPracticesFlag")
    ) {
        return (codes: T[]) =>
            codes
                .map(code => {
                    return {
                        id: code,
                        name: this._appDefinitions.getDisplayName(defKey, code),
                        data: {
                            color:
                                Object.keys(this._appDefinitions[defKey])
                                    .map(key => this._appDefinitions[defKey][key])
                                    .find(x => x.code === +code)?.type ?? 0
                        }
                    };
                })
                .sort((a, b) => {
                    if (b.data.color === 0) return -2;
                    else return a.data.color < b.data.color ? -1 : 1;
                });
    }

    flagsFilterNum(
        id: string,
        params: Combo2Parameters<number>,
        nameLC: string,
        defKey: DefinitionKey & ("flag" | "bestPracticesFlag")
    ) {
        return this.createCombo2Number(id, params, {
            nameLC,
            mapToOptions: this._mapToFlagsOption(defKey)
        });
    }

    flagsFilterStr(
        id: string,
        params: Combo2Parameters<string>,
        nameLC: string,
        defKey: DefinitionKey & ("flag" | "bestPracticesFlag")
    ) {
        return this.createCombo2String(id, params, {
            nameLC,
            mapToOptions: this._mapToFlagsOption(defKey)
        });
    }

    // ---------------------------------------------------------------------------------------------
    addFilter<T extends SupportedFilters, N extends string>(
        id: N,
        params: T
    ): FilterFactoryCreator<
        Definitions & { [P in N]: MapFilterDefinition<T> },
        Filters & { [P in N]: MapFilterStore<T> }
    > {
        return this._addFilter(id, params);
    }

    addMappedNumberCombo2Filter<T extends string>(
        id: T,
        params: Combo2Parameters<number> & {
            optionName: (id: number) => string;
            optionOrderById?: boolean;
        }
    ) {
        return this.createCombo2Number(id, params, {
            mapToOptions: ids =>
                _.chain(ids)
                    .map(id => {
                        const name = params.optionName(id);
                        return { id, name, sort: params.optionOrderById ? id : name };
                    })
                    .sortBy(f => f.sort)
                    .value()
        });
    }

    addMappedStringCombo2Filter<T extends string>(
        id: T,
        params: Combo2Parameters<string> & {
            optionName: (id: string) => string;
            optionOrderById?: boolean;
        }
    ) {
        return this.createCombo2String(id, params, {
            mapToOptions: ids =>
                _.chain(ids)
                    .map(id => {
                        const name = params.optionName(id);
                        return { id, name, sort: params.optionOrderById ? id : name };
                    })
                    .sortBy(f => f.sort)
                    .value()
        });
    }

    addCheckboxFilter<T extends string>(id: T, params: CheckboxParameters) {
        return this.createCheckbox(id, params, null);
    }

    /* addErrorFilter<T extends string>( id: T, params: CheckboxParameters ) {
        return this.createCheckbox(
            id,
            params,
            {
                nameLC: "APP._Filters.Errors"
            }
        );
    }*/

    // ---------------------------------------------------------------------------------------------
    //  Private helpers
    // ---------------------------------------------------------------------------------------------

    private createCombo2Number<T extends string>(
        id: T,
        params: Combo2Parameters<number>,
        defaults: Partial<IFilterFactoryEntry<IComboFilter2Definition<number>>>
    ) {
        return this.addFilter(id, {
            filterType: "combo2",
            main: true,
            idType: "number",
            visible: () => true,
            ...{
                showSelected: FilterFactoryCreator.MAX_TAGS_SHOWN_DEFAULT,
                maxRows: FilterFactoryCreator.TAGS_LINES_DEFAULT,
                ...defaults
            },
            ...params
        } as IFilterFactoryEntry<IComboFilter2Definition<number>>);
    }

    private createCombo2String<T extends string>(
        id: T,
        params: Combo2Parameters<string>,
        defaults: Partial<IFilterFactoryEntry<IComboFilter2Definition<string>>>
    ) {
        return this.addFilter(id, {
            filterType: "combo2",
            main: true,
            idType: "string",
            visible: () => true,
            ...{
                showSelected: FilterFactoryCreator.MAX_TAGS_SHOWN_DEFAULT,
                maxRows: FilterFactoryCreator.TAGS_LINES_DEFAULT,
                ...defaults
            },
            ...params
        } as IFilterFactoryEntry<IComboFilter2Definition<string>>);
    }

    private createCheckbox<T extends string>(
        id: T,
        params: CheckboxParameters,
        defaults: Partial<IFilterFactoryEntry<ICheckboxFilterDefinition>> | null
    ) {
        return this.addFilter(id, {
            filterType: "checkbox",
            main: true,
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<ICheckboxFilterDefinition>);
    }

    private createMapper<T extends number | string>(definition: DefinitionKey) {
        return (codes: T[]) =>
            _.chain(codes)
                .map(code => {
                    return {
                        id: code,
                        name: this._appDefinitions.getDisplayName(definition, code),
                        order: this._appDefinitions.getOrderBy(definition, code)
                    };
                })
                .sortBy(f => f.order)
                .value();
    }

    private createDictionaryMapper<T extends number | string>(definition: _.Dictionary<string>) {
        return (codes: T[]) =>
            _.chain(codes)
                .map(code => {
                    return { id: code, name: definition[code], order: code };
                })
                .sortBy(f => f.order)
                .value();
    }
}
